import { ClientCreation, ClientCreationWithErrors, ClientRaw, ErrorUploadClient } from '@Arti-zen/package-backoffice'
import { CircularProgress } from '@mui/material'
import { useAppSelector } from 'hooks/useStore'
import { pages } from 'navigation/pages'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { Theme } from 'theme/theme'
import TableClients from './TableClients'
import { sendClientsCreationByChunk } from '../clients.helper'
import ClientsAfterUploading from './ClientsAfterUploading'

type Result = {
  success: boolean
  clients?: ClientCreation[]
  clientsWithErrors?: ClientCreationWithErrors[]
  error?: string
  duplicates?: ClientRaw[]
}

export default function ArtisanUploadClients() {
  const { artisan } = useAppSelector((state) => state.artisan)
  const { state } = useLocation() as { state: { result: Result; onModify: boolean } }

  const [clientsWithErrors, setClientsWithErrors] = useState<ClientCreationWithErrors[]>(state.result.clientsWithErrors)
  const [clients, setClients] = useState<ClientCreation[]>(state.result.clients)

  const [message, setMessage] = useState<{ error: boolean; text: string } | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [finalCSV, setFinalCSV] = useState<string | null>(null)
  const [progress, setProgress] = useState<number>(0)

  const onModify = state.onModify

  const uploadingEnded = !!message && message.error === false

  const navigate = useNavigate()

  useEffect(() => {
    if (!state) {
      navigate(pages.connected.artisan.upload.navigate(artisan.id))
    }
  }, [state])

  const getErrorMessage = (err: ErrorUploadClient) => {
    switch (err.type) {
      case 'NO_CLIENTS':
        return 'Aucun client à importer'
      case 'CLIENT_WITH_ERRORS':
        return 'Certains des clients à importer contiennent encore des erreurs'
      case 'PROCESSING_ERROR':
        return "Problème lors de l'importation des clients."
      case 'DUPLICATE_CLIENTS':
        return 'Certains clients sont en doublon.'
    }
  }

  const onSubmit = async () => {
    setIsLoading(true)
    setMessage(null)

    const res = await sendClientsCreationByChunk({
      clientsToSend: clients,
      companyId: String(artisan.id),
      onModify,
      onProgress: (progress) => setProgress(progress),
    })

    if (!res.success) {
      setClients(res.clients)
      setClientsWithErrors(res.clientsWithErrors)
      if (res.error) {
        setMessage({ error: true, text: getErrorMessage(res.error) })
      }
      setIsLoading(false)
      return
    }

    setFinalCSV(res.csv)
    setIsLoading(false)
    setMessage({ error: false, text: `Les clients ont bien été ${onModify ? 'modifiés' : 'importés'}` })
  }

  if (isLoading)
    return (
      <LoaderContainer>
        <CircularProgress size={70} color="inherit" />
        <p>{`${progress} clients ${onModify ? 'modifiés' : 'importés'} sur ${clients.length}`}</p>
      </LoaderContainer>
    )

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Row>
        <Title>{onModify ? 'Modifier' : 'Importer'} les clients</Title>
      </Row>

      {message && <p style={{ color: message?.error ? 'red' : '#19d692' }}>{message.text}</p>}

      {uploadingEnded ? (
        <ClientsAfterUploading clients={clients} onModify={onModify} csv={finalCSV} />
      ) : (
        <TableClients
          clients={clients}
          clientsWithErrors={clientsWithErrors}
          setClients={setClients}
          setClientsWithErrors={setClientsWithErrors}
          duplicates={state.result.duplicates ?? []}
          onSubmit={onSubmit}
          onModify={onModify}
        />
      )}
    </div>
  )
}

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 15px;
`
export const Title = styled.p`
  font-family: ${Theme.font.family};
  font-size: 15px;
  font-weight: 600;
  color: ${Theme.colors.blackArtizen};
`

const LoaderContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${Theme.colors.blueArtizen};
  margin-top: -100px;
`
